import type { SortDescriptor } from "react-stately";

import { RequestTypeEnum } from "@nlc/shared-types";
import { useCallback, useState } from "react";

import type { SuperAdminRequestType } from "../config/requestTypeTitles";

/**
 * Individual table state for each request type
 */
export interface TableState {
  currentPage: number;
  itemsPerPage: number;
  sortDescriptor: SortDescriptor;
}

/**
 * State mapping for SuperAdmin request types only
 */
export type PerRequestTypeTableStates = {
  [K in SuperAdminRequestType]: TableState;
};

/**
 * Default table state configuration
 */
const DEFAULT_TABLE_STATE: TableState = {
  currentPage: 1,
  itemsPerPage: 10, // Default to 10 entries per table as requested
  sortDescriptor: {
    column: "createdAt",
    direction: "descending",
  },
};

/**
 * Initial states for all request types
 */
const INITIAL_TABLE_STATES: PerRequestTypeTableStates = {
  [RequestTypeEnum.GRAPHIC]: { ...DEFAULT_TABLE_STATE },
  [RequestTypeEnum.VIDEO]: { ...DEFAULT_TABLE_STATE },
  [RequestTypeEnum.WEBSITE_DESIGN]: { ...DEFAULT_TABLE_STATE },
  [RequestTypeEnum.WEBSITE_IMPLEMENTATION]: { ...DEFAULT_TABLE_STATE },
};

/**
 * Hook for managing individual table states for each request type
 * Ensures each table (Graphic, Video, Website Design, Website Implementation)
 * maintains its own pagination, sorting, and other state independently
 */
export function usePerRequestTypeTableStates(
  defaultSortColumn?: string,
  defaultSortDirection?: "ascending" | "descending",
) {
  // Initialize states with custom defaults if provided
  const initialStates =
    defaultSortColumn || defaultSortDirection
      ? (Object.fromEntries(
          Object.entries(INITIAL_TABLE_STATES).map(([requestType, state]) => [
            requestType,
            {
              ...state,
              sortDescriptor: {
                column: defaultSortColumn || state.sortDescriptor.column,
                direction:
                  defaultSortDirection || state.sortDescriptor.direction,
              },
            },
          ]),
        ) as PerRequestTypeTableStates)
      : INITIAL_TABLE_STATES;

  const [tableStates, setTableStates] =
    useState<PerRequestTypeTableStates>(initialStates);

  /**
   * Update state for a specific request type table
   */
  const updateTableState = useCallback(
    (requestType: SuperAdminRequestType, updates: Partial<TableState>) => {
      setTableStates((prev) => ({
        ...prev,
        [requestType]: {
          ...prev[requestType],
          ...updates,
        },
      }));
    },
    [],
  );

  /**
   * Reset all table states to initial values
   */
  const resetTableStates = useCallback(() => {
    setTableStates(initialStates);
  }, [initialStates]);

  /**
   * Get state for a specific request type
   */
  const getTableState = useCallback(
    (requestType: SuperAdminRequestType): TableState =>
      tableStates[requestType],
    [tableStates],
  );

  /**
   * Create handlers for a specific request type table
   */
  const createTableHandlers = useCallback(
    (requestType: SuperAdminRequestType) => ({
      onItemsPerPageChange: (itemsPerPage: number) => {
        updateTableState(requestType, {
          currentPage: 1, // Reset to first page when changing page size
          itemsPerPage,
        });
      },
      onPageChange: (page: number) => {
        updateTableState(requestType, { currentPage: page });
      },
      onSortChange: (sortDescriptor: SortDescriptor) => {
        updateTableState(requestType, { sortDescriptor });
      },
    }),
    [updateTableState],
  );

  return {
    createTableHandlers,
    getTableState,
    resetTableStates,
    tableStates,
    updateTableState,
  };
}

/**
 * Helper type for table handlers
 */
export type TableHandlers = ReturnType<
  typeof usePerRequestTypeTableStates
>["createTableHandlers"] extends (...args: unknown[]) => infer R
  ? R
  : never;
